<template>
  <div class="ai-visibility-category-step">
    <step-intro
        title="What should AI associate your brand with?"
        description="Don’t overthink it. Pick 1–3 categories your customers search for."
    />

    <div class="ai-visibility-category-field">
      <label class="ai-visibility-category-field__copy" :for="categoryInputId">
        <span class="ai-visibility-category-field__label">
          Category* <span>(maximum 3)</span>
        </span>
        <span class="ai-visibility-category-field__helper">
          Think “what people want to buy or learn,” not SEO keywords.
        </span>
      </label>
      <div
          class="ai-visibility-category-field__input"
          :class="{ 'ai-visibility-category-field__input--error': validationErrors.categories }"
      >
        <span
            v-for="category in selectedCategories"
            :key="category.id"
            class="ai-visibility-category-pill"
        >
          {{ category.label }}
          <button type="button" title="Remove category" @click.stop="removeCategory(category.id)">
            <i class="material-icons">close</i>
          </button>
        </span>
        <input
            :id="categoryInputId"
            v-model="categoryText"
            :disabled="selectedCategories.length >= 3"
            :placeholder="categoryPlaceholder"
            @keydown.enter.prevent="addCategoryFromText"
            @blur="addCategoryFromText"
            @keydown.188.prevent="addCategoryFromText"
        />
      </div>
    </div>

    <div v-if="validationErrors.categories" class="ai-visibility-error">{{ validationErrors.categories }}</div>

    <p v-if="availableSuggestions.length" class="ai-visibility-category-suggestions">
      <span class="ai-visibility-category-suggestions__label">Suggestions: </span>
      <button
          v-for="(category, index) in availableSuggestions"
          :key="category.id"
          type="button"
          :disabled="selectedCategories.length >= 3"
          @click="addSuggestion(category)"
      >
        {{ category.label }}{{ index < availableSuggestions.length - 1 ? ', ' : '' }}
      </button>
    </p>

    <div class="local-business-warning">
      <div class="local-business-warning_icon">
        <span class="material-icons-outlined">
        location_on
        </span>
      </div>
      <div class="local-business-warning_body">
        <div class="local-business-warning__message">
          <span class="local-business-warning__title">Local business? </span>
          <span class="local-business-warning__text">Add your city or area to the category so we ask questions the way nearby customers do.</span>
        </div>
        <div class="local-business-warning__example">Example: "plumber" → "plumber in Austin" · "coffee shop" → "coffee
          shop in Seattle"
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepIntro from '../ui/StepIntro.vue'

let categoryInputId = 0

export default {
  name: 'CategoryStep',
  components: {
    StepIntro,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    selectedCategories: {
      type: Array,
      default: () => [],
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      categoryInputId: `ai-visibility-category-input-${categoryInputId += 1}`,
      categoryText: '',
    }
  },
  computed: {
    categoryPlaceholder() {
      return this.selectedCategories.length
          ? ''
          : 'e.g., sport cars, running shoes, email marketing'
    },
    availableSuggestions() {
      const selectedIds = new Set(this.selectedCategories.map((category) => category.id))
      return this.categories.filter((category) => !selectedIds.has(category.id))
    },
  },
  methods: {
    filterCategories(category) {
      const seen = new Set()
      const filteredArr = category.filter(el => {
        const duplicate = seen.has(el.label.toLowerCase());
        seen.add(el.label.toLowerCase());
        return !duplicate;
      });
      return filteredArr.slice(0, 3);
    },
    setCategories(categories) {
      this.$emit('update:selectedCategories', this.filterCategories(categories));
    },
    addSuggestion(category) {
      if (this.selectedCategories.length >= 3) {
        return
      }

      this.setCategories(this.selectedCategories.concat(category))
    },
    addCategoryFromText() {
      const text = this.categoryText.trim().replace(/,$/, '')
      const categoriesText = this.categoryText.split(',').map((text, index) => {
        return {
          id: `custom-category-${index}-${Date.now()}`,
          label: text,
          custom: true,
        }
      });

      if (!text || this.selectedCategories.length >= 3) {
        return
      }
      this.setCategories(this.selectedCategories.concat(categoriesText))
      this.categoryText = ''
    },
    removeCategory(categoryId) {
      this.setCategories(this.selectedCategories.filter((category) => category.id !== categoryId))
    },
  },
}
</script>

<style lang="scss" scoped>

.local-business-warning {
  background-color: var(--v-lightYellowColor-base);
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border: 1px solid rgba(255, 87, 0, .22);
  border-radius: 12px;
  padding: 15px 17px;
  margin-top: 6px;
  margin-bottom: 6px;


  .local-business-warning_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--v-whiteColor-base);
    min-width: 26px;
    height: 26px;
    border-radius: 7px;


    .material-icons-outlined {
      color: var(--v-profileAnchorColor-base);
      font-size: 16px;
    }
  }

  .local-business-warning_body {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .local-business-warning__message {
      font-size: 16px;
      color: #7a3b12;

      .local-business-warning__title {
        font-weight: 700;
        color: #a8480e;
      }
    }

    .local-business-warning__example {
      color: #955021;
    }
  }

}

.ai-visibility-category-step {
  display: flex;
  min-height: 100%;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  font-family: Mulish, sans-serif;
}

.ai-visibility-category-field {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 10px;
}

.ai-visibility-category-field__copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  font-weight: 400;
}

.ai-visibility-category-field__label {
  color: var(--v-mainColor-base);
  font-size: 16px;
  line-height: 1.5;

  span {
    color: var(--v-darkGrayColor-base);
  }
}

.ai-visibility-category-field__helper {
  min-height: 15px;
  color: var(--v-gray2Color-base);
  font-size: 12px;
  letter-spacing: 0.12px;
  line-height: normal;
}

.ai-visibility-category-field__input {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  min-height: 42px;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  border: 1px solid #DADADA;
  border-radius: 4px;
  background: var(--v-cardBg-base);
  padding: 10px;

  input {
    box-sizing: border-box;
    min-width: 0;
    height: 22px;
    flex: 1;
    border: 0;
    color: var(--v-mainColor-base);
    font-size: 14px;
    line-height: 22px;
    outline-color: #DADADA;
    padding: 0;

    &::placeholder {
      color: var(--v-darkGrayColor-base);
      opacity: 1;
    }
  }
}

.ai-visibility-category-field__input--error {
  border-color: var(--v-red11Color-base);
}

.ai-visibility-category-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 30px;
  background: var(--v-lightYellowColor-base);
  color: var(--v-mainColor-base);
  font-size: 14px;
  line-height: 1.4;
  padding: 5px 8px 5px 10px;

  button {
    display: inline-flex;
    width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--v-mainColor-base);
    cursor: pointer;
    padding: 0;
  }

  i {
    font-size: 14px;
  }
}

.ai-visibility-muted {
  color: var(--v-gray2Color-base);
  font-size: 12px;
}

.ai-visibility-error {
  color: var(--v-red11Color-base);
  font-size: 12px;
  margin-top: 10px;
}

.ai-visibility-category-suggestions {
  width: 100%;
  color: var(--v-mainColor3-base);
  font-size: 0;
  font-weight: 500;
  line-height: normal;
  margin: 0;
}

.ai-visibility-category-suggestions__label,
.ai-visibility-category-suggestions button {
  font-size: 14px;
}

.ai-visibility-category-suggestions button {
  border: 0;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  font-family: inherit;
  font-weight: inherit;
  line-height: inherit;
  padding: 0 2px;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:hover {
    color: var(--v-profileAnchorColor-base);
    text-decoration: underline;
  }
}
</style>
